import React, { useState } from 'react';
import { X, Search, CheckCircle, Clock, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/VisaStatusModal.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VisaStatusModal = ({ onClose }) => {
  const { language } = useLanguage();
  const [applicationNumber, setApplicationNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: "Check Visa Status",
      subtitle: "Enter your application number to check your visa status",
      placeholder: "Your Name + Number (e.g., RAMIZ20260001)",
      checkButton: "Check Status",
      checking: "Checking...",
      errorEmpty: "Please enter your application number",
      errorNotFound: "Application not found. Please check your number and try again.",
      errorServer: "Unable to check status. Please try again later.",
      statusLabels: {
        pending: "Pending Review",
        checked: "Under Review",
        processing: "In Processing",
        approved: "Approved",
        rejected: "Rejected",
        document_required: "Document Required",
        pending_payment: "Pending Payment",
        completed: "Completed"
      },
      statusMessages: {
        pending: "Your application is pending review. We will process it shortly.",
        checked: "Your application is being reviewed by our team.",
        processing: "Your application is being processed. Please wait for updates.",
        approved: "Congratulations! Your visa has been approved.",
        rejected: "Unfortunately, your application was rejected. Please contact support.",
        document_required: "Additional documents are required. Please check your email or contact us.",
        pending_payment: "Your application is awaiting payment. Please complete the payment to proceed.",
        completed: "Your visa process is complete. Please collect your documents."
      },
      contactSupport: "Contact Support",
      tryAnother: "Check Another",
      applicantLabel: "Applicant:",
      serviceLabel: "Service:"
    },
    ru: {
      title: "Проверить статус визы",
      subtitle: "Введите номер заявки для проверки статуса визы",
      placeholder: "Ваше Имя + Номер (напр., RAMIZ20260001)",
      checkButton: "Проверить статус",
      checking: "Проверка...",
      errorEmpty: "Пожалуйста, введите номер заявки",
      errorNotFound: "Заявка не найдена. Проверьте номер и попробуйте снова.",
      errorServer: "Не удалось проверить статус. Попробуйте позже.",
      statusLabels: {
        pending: "На рассмотрении",
        checked: "На проверке",
        processing: "В обработке",
        approved: "Одобрено",
        rejected: "Отклонено",
        document_required: "Требуется документ",
        pending_payment: "Ожидает оплаты",
        completed: "Завершено"
      },
      statusMessages: {
        pending: "Ваша заявка находится на рассмотрении. Мы обработаем её в ближайшее время.",
        checked: "Ваша заявка проверяется нашей командой.",
        processing: "Ваша заявка обрабатывается. Пожалуйста, ожидайте обновлений.",
        approved: "Поздравляем! Ваша виза одобрена.",
        rejected: "К сожалению, ваша заявка отклонена. Свяжитесь с поддержкой.",
        document_required: "Требуются дополнительные документы. Проверьте email или свяжитесь с нами.",
        pending_payment: "Ваша заявка ожидает оплаты. Пожалуйста, завершите оплату для продолжения.",
        completed: "Процесс оформления визы завершён. Заберите документы."
      },
      contactSupport: "Связаться с поддержкой",
      tryAnother: "Проверить другую",
      applicantLabel: "Заявитель:",
      serviceLabel: "Услуга:"
    },
    uz: {
      title: "Viza holatini tekshirish",
      subtitle: "Viza holatini tekshirish uchun ariza raqamini kiriting",
      placeholder: "Ismingiz + Raqam (masalan, RAMIZ20260001)",
      checkButton: "Holatni tekshirish",
      checking: "Tekshirilmoqda...",
      errorEmpty: "Iltimos, ariza raqamini kiriting",
      errorNotFound: "Ariza topilmadi. Raqamni tekshirib, qaytadan urinib ko'ring.",
      errorServer: "Holatni tekshirib bo'lmadi. Keyinroq urinib ko'ring.",
      statusLabels: {
        pending: "Ko'rib chiqilmoqda",
        checked: "Tekshirilmoqda",
        processing: "Jarayonda",
        approved: "Tasdiqlangan",
        rejected: "Rad etilgan",
        document_required: "Hujjat kerak",
        pending_payment: "To'lov kutilmoqda",
        completed: "Yakunlangan"
      },
      statusMessages: {
        pending: "Arizangiz ko'rib chiqilmoqda. Tez orada qayta ishlaymiz.",
        checked: "Arizangiz jamoamiz tomonidan tekshirilmoqda.",
        processing: "Arizangiz qayta ishlanmoqda. Yangilanishlarni kuting.",
        approved: "Tabriklaymiz! Vizangiz tasdiqlandi.",
        rejected: "Afsuski, arizangiz rad etildi. Qo'llab-quvvatlash bilan bog'laning.",
        document_required: "Qo'shimcha hujjatlar kerak. Emailingizni tekshiring yoki biz bilan bog'laning.",
        pending_payment: "Arizangiz to'lovni kutmoqda. Davom etish uchun to'lovni amalga oshiring.",
        completed: "Viza jarayoni yakunlandi. Hujjatlaringizni oling."
      },
      contactSupport: "Qo'llab-quvvatlash",
      tryAnother: "Boshqasini tekshirish",
      applicantLabel: "Ariza beruvchi:",
      serviceLabel: "Xizmat:"
    }
  };

  const currentContent = content[language] || content.en;

  const handleCheck = async () => {
    if (!applicationNumber.trim()) {
      setError(currentContent.errorEmpty);
      return;
    }

    setIsLoading(true);
    setError('');
    setStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/bookings/status/${encodeURIComponent(applicationNumber.trim())}`);
      
      if (response.ok) {
        const data = await response.json();
        setStatus({
          applicationNumber: data.applicationNumber,
          status: data.status,
          date: data.lastUpdated ? new Date(data.lastUpdated).toLocaleDateString() : new Date().toLocaleDateString(),
          applicantName: data.applicantName,
          service: data.service
        });
      } else if (response.status === 404) {
        setError(currentContent.errorNotFound);
      } else {
        setError(currentContent.errorServer);
      }
    } catch (err) {
      console.error('Error checking status:', err);
      setError(currentContent.errorServer);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (statusType) => {
    switch (statusType) {
      case 'approved':
      case 'completed':
        return <CheckCircle size={32} />;
      case 'processing':
      case 'checked':
        return <Clock size={32} />;
      case 'pending':
        return <FileText size={32} />;
      case 'rejected':
      case 'document_required':
        return <AlertCircle size={32} />;
      case 'pending_payment':
        return <Clock size={32} />;
      default:
        return <FileText size={32} />;
    }
  };

  const getStatusColor = (statusType) => {
    switch (statusType) {
      case 'approved':
      case 'completed':
        return '#2E7D32';
      case 'processing':
      case 'checked':
        return '#FFD700';
      case 'pending':
        return '#60A5FA';
      case 'rejected':
        return '#EF4444';
      case 'document_required':
        return '#F97316';
      case 'pending_payment':
        return '#A855F7';
      default:
        return '#60A5FA';
    }
  };

  const handleReset = () => {
    setApplicationNumber('');
    setStatus(null);
    setError('');
  };

  return (
    <div className="visa-status-modal-overlay" onClick={onClose}>
      <div className="visa-status-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="visa-status-close" onClick={onClose}>
          <X size={24} />
        </button>

        {/* Animated Background */}
        <div className="visa-status-bg">
          <div className="status-circle circle-1"></div>
          <div className="status-circle circle-2"></div>
        </div>

        {/* Content */}
        <div className="visa-status-content">
          {/* Header */}
          <div className="visa-status-header">
            <div className="status-icon-wrapper">
              <Search size={28} />
            </div>
            <h2>{currentContent.title}</h2>
            <p>{currentContent.subtitle}</p>
          </div>

          {/* Input Section */}
          {!status && (
            <div className="visa-status-input-section">
              <div className="input-wrapper">
                <input
                  type="text"
                  value={applicationNumber}
                  onChange={(e) => {
                    setApplicationNumber(e.target.value.toUpperCase());
                    setError('');
                  }}
                  placeholder={currentContent.placeholder}
                  className={error ? 'error' : ''}
                  onKeyPress={(e) => e.key === 'Enter' && handleCheck()}
                />
                {error && <span className="error-message">{error}</span>}
              </div>
              <button 
                className="check-status-btn"
                onClick={handleCheck}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="spinner" />
                    {currentContent.checking}
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    {currentContent.checkButton}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Status Result */}
          {status && (
            <div className="visa-status-result">
              <div 
                className="status-badge"
                style={{ 
                  backgroundColor: `${getStatusColor(status.status)}20`,
                  borderColor: getStatusColor(status.status),
                  color: getStatusColor(status.status)
                }}
              >
                {getStatusIcon(status.status)}
                <span className="status-label">
                  {currentContent.statusLabels[status.status] || status.status}
                </span>
              </div>
              
              <div className="status-details">
                <div className="detail-row">
                  <span className="detail-label">Application #:</span>
                  <span className="detail-value">{status.applicationNumber}</span>
                </div>
                {status.applicantName && (
                  <div className="detail-row">
                    <span className="detail-label">{currentContent.applicantLabel}</span>
                    <span className="detail-value">{status.applicantName}</span>
                  </div>
                )}
                {status.service && (
                  <div className="detail-row">
                    <span className="detail-label">{currentContent.serviceLabel}</span>
                    <span className="detail-value">{status.service}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">{status.date}</span>
                </div>
              </div>

              <p className="status-message">
                {currentContent.statusMessages[status.status] || "Status information available."}
              </p>

              <div className="status-actions">
                <a 
                  href="https://wa.me/971569130707" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="contact-btn"
                >
                  {currentContent.contactSupport}
                </a>
                <button className="try-another-btn" onClick={handleReset}>
                  {currentContent.tryAnother}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisaStatusModal;
